'use client'

import type { ComponentProps } from 'react'
import { useParams } from 'next/navigation'
import { useLocale, useTranslations } from 'next-intl'
import { Link, usePathname } from '@/i18n/navigation'
import { routing, type Locale } from '@/i18n/routing'
import { cn } from '@/lib/cn'

interface LanguageSwitcherProps {
  className?: string
  /** `onDark` pour les fonds sombres (footer). */
  tone?: 'default' | 'onDark'
}

/**
 * Sélecteur de langue.
 *
 * Il pointe vers le même chemin traduit, en réutilisant le slug courant. Quand
 * le slug diffère d'une langue à l'autre — `/domaines/btp` contre
 * `/en/industries/construction` — c'est la PAGE CIBLE qui rétablit l'adresse,
 * par une redirection 301 permanente. Le correctif est ainsi côté serveur :
 * il vaut aussi pour un lien externe mal formé, et il fonctionne sans
 * JavaScript.
 */
export function LanguageSwitcher({ className, tone = 'default' }: LanguageSwitcherProps) {
  const pathname = usePathname()
  const params = useParams()
  const activeLocale = useLocale() as Locale
  const t = useTranslations()

  return (
    <div
      className={cn(
        'inline-flex items-center rounded-full p-0.5 ring-1',
        tone === 'onDark' ? 'bg-white/5 ring-white/15' : 'ring-border bg-surface',
        className,
      )}
      role="group"
      aria-label={t('common.languageSwitcher')}
    >
      {routing.locales.map((locale) => {
        const isActive = locale === activeLocale

        return (
          <Link
            key={locale}
            href={{ pathname, params } as ComponentProps<typeof Link>['href']}
            locale={locale}
            hrefLang={locale}
            aria-current={isActive ? 'true' : undefined}
            className={cn(
              'rounded-full px-2.5 py-1 text-xs font-semibold transition-colors duration-200',
              isActive
                ? 'bg-primary-500 text-white'
                : tone === 'onDark'
                  ? 'text-text-ondark-muted hover:bg-white/10 hover:text-white'
                  : 'text-text-muted hover:text-primary-700 hover:bg-primary-50',
            )}
          >
            <span aria-hidden="true">{t(`locale.${locale}Short`)}</span>
            <span className="sr-only">{t(`locale.${locale}`)}</span>
          </Link>
        )
      })}
    </div>
  )
}
